// frontend\src\components\InputsCoverChart.tsx
// Days of cover vs reorder point, per input. Where the grey bar overtakes the
// coloured one, the input needs ordering now.
import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Input, InputHealth } from '../types';
import ErrorBoundary from './ErrorBoundary';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Matched by hand to the .stock-health-* chips in core.css
const HEALTH_COLORS: Record<InputHealth, string> = {
  out: '#E53935',
  critical: '#E53935',
  low: '#D9A227',
  ok: '#4CAF50',
  na: '#B8B8B2',
};

// Sized for the full expected roster (~50). The canvas grows with the row
// count and scrolls inside a capped wrapper rather than stretching the page.
const MAX_BARS = 50;
const BAR_HEIGHT_PX = 26;

interface InputsCoverChartProps {
  inputs: Input[];
}

const InputsCoverChart: React.FC<InputsCoverChartProps> = ({ inputs }) => {
  // Only inputs with a computable burn rate can have a cover figure.
  // Ascending days of cover puts the most urgent input in the first slot, which
  // Chart.js renders at the top of a horizontal bar chart.
  const charted = inputs
    .filter((input) => input.days_cover !== null)
    .sort((a, b) => (a.days_cover as number) - (b.days_cover as number))
    .slice(0, MAX_BARS);

  const hidden = inputs.filter((input) => input.days_cover !== null).length - charted.length;

  if (charted.length === 0) {
    return (
      <div className="inputs-chart-card">
        <h3>Days of Cover</h3>
        <p className="dash-empty">
          No input has a burn rate yet. Add a usage rule so cover can be calculated.
        </p>
      </div>
    );
  }

  const data = {
    labels: charted.map((input) => input.name),
    datasets: [
      {
        label: 'Days of cover',
        data: charted.map((input) => input.days_cover as number),
        backgroundColor: charted.map((input) => HEALTH_COLORS[input.health]),
        borderRadius: 3,
      },
      {
        label: 'Reorder point (lead + buffer)',
        data: charted.map((input) => input.delivery_days + input.safety_days),
        backgroundColor: '#C5D9F5',
        borderRadius: 3,
      },
    ],
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${Math.round(context.parsed.x)} days`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: 'Days' },
        grid: { color: '#E8E8E4' },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
      },
    },
  };

  return (
    <div className="inputs-chart-card">
      <h3>Days of Cover</h3>
      <p className="dash-card-note">
        Most urgent first. When the grey bar is longer than the coloured one, the input will
        run out before a replacement order could arrive.
        {hidden > 0 && ` Showing the ${charted.length} lowest of ${charted.length + hidden}.`}
      </p>
      <ErrorBoundary>
        <div className="inputs-chart-scroll">
          <div
            className="inputs-chart-canvas"
            style={{ height: `${Math.max(charted.length * BAR_HEIGHT_PX, 160)}px` }}
          >
            <Bar data={data} options={options} />
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default InputsCoverChart;
